import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { Meal, MealItem } from '@/types';
import { startOfDay, endOfDay, format } from 'date-fns';
import { isPlannedAhead } from '@/lib/mealDay';
export function useDayMeals(date: Date = new Date()) {
  const [meals, setMeals] = useState<Meal[]>([]);
  const [loading, setLoading] = useState(true);
  const dateStr = format(date, 'yyyy-MM-dd');
  const dateStrRef = useRef(dateStr);
  const refetch = useCallback(async () => {
    setLoading(true);
    const d = new Date(dateStrRef.current);
    const { data, error } = await supabase
      .from('meals')
      .select('*, items:meal_items(*)')
      .gte('consumed_at', startOfDay(d).toISOString())
      .lte('consumed_at', endOfDay(d).toISOString())
      .order('consumed_at', { ascending: true });
    if (!error && data) setMeals(data as Meal[]);
    setLoading(false);
  }, []);
  useEffect(() => {
    dateStrRef.current = dateStr;
    refetch();
  }, [dateStr, refetch]);
  const totals = meals.reduce(
    (acc, m) => ({
      calories: acc.calories + (m.total_calories ?? 0),
      protein: acc.protein + Number(m.total_protein_g ?? 0),
      fat: acc.fat + Number(m.total_fat_g ?? 0),
      carbs: acc.carbs + Number(m.total_carbs_g ?? 0),
    }),
    { calories: 0, protein: 0, fat: 0, carbs: 0 },
  );
  return { meals, totals, loading, refetch };
}
// Результат createMeal — теперь возвращает информацию о ачивках/XP
export interface CreateMealResult {
  meal: Meal;
  gamification?: {
    xp_added: number;
    level_up: boolean;
    new_level: number;
    achievements: Array<{
      id: string;
      title: string;
      icon: string;
      xp_reward: number;
    }>;
  };
}
export async function createMeal(params: {
  meal_type: Meal['meal_type'];
  source: Meal['source'];
  items: MealItem[];
  photo_url?: string;
  raw_input?: string;
  ai_confidence?: number;
  consumed_at?: Date;
}): Promise<Meal> {
  const totals = params.items.reduce(
    (a, i) => ({
      cal: a.cal + (i.calories || 0),
      p: a.p + Number(i.protein_g || 0),
      f: a.f + Number(i.fat_g || 0),
      c: a.c + Number(i.carbs_g || 0),
    }),
    { cal: 0, p: 0, f: 0, c: 0 },
  );
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Не авторизован');
  
  const consumedAt = params.consumed_at 
    ? params.consumed_at.toISOString() 
    : new Date().toISOString();
  
  const { data: meal, error } = await supabase
    .from('meals')
    .insert({
      user_id: user.id,
      meal_type: params.meal_type,
      source: params.source,
      photo_url: params.photo_url,
      raw_input: params.raw_input,
      ai_confidence: params.ai_confidence,
      total_calories: Math.round(totals.cal),
      total_protein_g: totals.p,
      total_fat_g: totals.f,
      total_carbs_g: totals.c,
      consumed_at: consumedAt,
    })
    .select()
    .single();
  if (error) throw error;
  
  if (params.items.length) {
    const { error: itemsErr } = await supabase.from('meal_items').insert(
      params.items.map((it: any, idx) => ({
        meal_id: meal.id,
        name: it.name,
        weight_g: it.weight_g,
        calories: Math.round(it.calories),
        protein_g: it.protein_g,
        fat_g: it.fat_g,
        carbs_g: it.carbs_g,
        position: idx,
        fiber_g: Number(it.fiber_g) || 0,
        vegetables_fruits_g: Number(it.vegetables_fruits_g) || 0,
        omega3_g: Number(it.omega3_g) || 0,
        saturated_fat_g: Number(it.saturated_fat_g) || 0,
        calcium_mg: Number(it.calcium_mg) || 0,
        iron_mg: Number(it.iron_mg) || 0,
        magnesium_mg: Number(it.magnesium_mg) || 0,
        zinc_mg: Number(it.zinc_mg) || 0,
        potassium_mg: Number(it.potassium_mg) || 0,
        vitamin_d_mcg: Number(it.vitamin_d_mcg) || 0,
        vitamin_b12_mcg: Number(it.vitamin_b12_mcg) || 0,
        vitamin_c_mg: Number(it.vitamin_c_mg) || 0,
        // 🆕 Компоненты составного блюда (роллы, шаурма и т.д.)
        // Если AI не вернул components — сохраняем пустой массив
        components: Array.isArray(it.components) ? it.components : [],
      })),
    );
    if (itemsErr) throw itemsErr;
  }
  
  // ГЕЙМИФИКАЦИЯ — асинхронно, не блокируем основной поток
  // Если ошибка — просто логируем, не прерываем сохранение.
  //
  // ⚠️ Запись на будущий день сюда не идёт (07.10.2026). Правило Славы: «если вношу
  // завтрашний день сегодня, для ачивок он наступит только по факту дня». Сервер это
  // соблюдает сам (миграция 057: триггер откладывает, фоновая задача засчитывает), но
  // этот вызов начислял серию и опыт напрямую с клиента — в обход отсрочки, и завтрашняя
  // еда засчитывала бы сегодняшний день.
  if (!isPlannedAhead(params.consumed_at)) {
    awardMealGamification(user.id, params).catch(err => {
      console.error('[createMeal] gamification error:', err);
    });
  }
  
  return meal as Meal;
}
// ============================================================
// ОБОГАЩЕНИЕ МИКРОНУТРИЕНТАМИ ПОСЛЕ СОХРАНЕНИЯ
// ============================================================
// Первичный анализ фото (fast) возвращает только КБЖУ + components.
// Клетчатка / овощи-фрукты / витамины / минералы досчитываются ПОСЛЕ
// сохранения и пишутся в колонки meal_items — их читает дневной экран
// (RPC get_daily_nutrients_summary). Сам досчёт живёт на сервере,
// в Edge-функции enrich-meal.

/**
 * Дозапросить микронутриенты для всех продуктов блюда и записать в meal_items.
 * Запускается в фоне сразу после сохранения — НЕ блокирует UX.
 * КБЖУ и components не трогаются: обновляются только микронутриентные колонки.
 */
export async function enrichMealNutrients(mealId: string): Promise<void> {
  // Раньше вся работа шла здесь, в браузере: по продукту на запрос к
  // recalculate-item. Если человек закрывал мини-апп раньше, чем цикл
  // добегал до конца, еда навсегда оставалась с нулями по витаминам —
  // молча, без ошибки. Теперь клиент только просит сервер начать; сервер
  // доводит до конца независимо от того, открыто приложение или нет,
  // а забытое подберёт ночной проход (enrich-meal, режим sweep).
  const { error } = await supabase.functions.invoke('enrich-meal', {
    body: { meal_id: mealId },
  });
  if (error) {
    console.error('[enrichMealNutrients] enrich-meal failed:', error);
    return;
  }

  // Досчёт занимает пару секунд, а экран успевает прочитать данные раньше —
  // и потом уже не перечитывает. Без этого сигнала витамины «не появляются»,
  // хотя в базе они есть.
  window.dispatchEvent(new CustomEvent(NUTRIENTS_UPDATED, { detail: { mealId } }));
}

// Досчёт микронутриентов закончился — тем, кто их показывает, пора обновиться
export const NUTRIENTS_UPDATED = 'nutrients:updated';

// Геймификация после добавления еды
/**
 * Вехи серии: на каждой человек получает достижение и может поделиться карточкой.
 * Идентификаторы должны совпадать с `streak_N` в таблице achievements (миграция 044).
 */
export const STREAK_MILESTONES = [3, 7, 14, 21, 30, 40, 50, 60, 70, 80, 90, 100, 150, 200];

async function awardMealGamification(
  userId: string,
  params: { source: string; consumed_at?: Date }
) {
  try {
    // 1. Обновляем стрик
    await supabase.rpc('update_meal_streak', { uid: userId });
    
    // 2. Начисляем XP
    // Базово +5 за приём, если фото — +3 (итого +8)
    const xpAmount = params.source === 'photo' ? 8 : 5;
    await supabase.rpc('add_xp', { uid: userId, amount: xpAmount });
    
    // 3. Проверяем first_meal
    await supabase.rpc('check_achievement', {
      uid: userId,
      ach_id: 'first_meal',
      new_progress: 1
    });

    // 3b. Объём и стаж — достижения, понятные снаружи (миграция 035):
    // «100 записей», «1000 записей», «100 дней со Славой».
    {
      const [{ count: mealsTotal }, { data: prof }] = await Promise.all([
        supabase.from('meals').select('id', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('profiles').select('created_at').eq('id', userId).maybeSingle(),
      ]);
      const total = mealsTotal || 0;
      if (total >= 100) await supabase.rpc('check_achievement', { uid: userId, ach_id: 'meals_100', new_progress: total });
      if (total >= 1000) await supabase.rpc('check_achievement', { uid: userId, ach_id: 'meals_1000', new_progress: total });
      if (prof?.created_at) {
        const days = Math.floor((Date.now() - new Date(prof.created_at).getTime()) / 86400000);
        if (days >= 100) await supabase.rpc('check_achievement', { uid: userId, ach_id: 'days_100', new_progress: days });
      }
    }
    
    // 4. Если фото — проверяем фото-ачивки
    if (params.source === 'photo') {
      // Считаем общее кол-во фото-приёмов
      const { count } = await supabase
        .from('meals')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('source', 'photo');
      
      const photoCount = count || 0;
      await supabase.rpc('check_achievement', { 
        uid: userId, 
        ach_id: 'first_photo', 
        new_progress: photoCount 
      });
      await supabase.rpc('check_achievement', { 
        uid: userId, 
        ach_id: 'photo_10', 
        new_progress: photoCount 
      });
      await supabase.rpc('check_achievement', { 
        uid: userId, 
        ach_id: 'photo_50', 
        new_progress: photoCount 
      });
    }
    
    // 5. Проверяем стрик-ачивки — берём текущий стрик
    const { data: profile } = await supabase
      .from('profiles')
      .select('current_streak_meals')
      .eq('id', userId)
      .single();
    
    const streak = profile?.current_streak_meals || 0;
    // Лесенка вех (миграция 044, решение Славы 21.09.2026): плотно в начале,
    // реже дальше. Список ОДИН — раньше это была стопка одинаковых `if`,
    // и каждая новая веха требовала копировать строку и не забыть про неё.
    for (const days of STREAK_MILESTONES) {
      if (streak >= days) {
        await supabase.rpc('check_achievement', {
          uid: userId,
          ach_id: `streak_${days}`,
          new_progress: streak,
        });
      }
    }
  } catch (e) {
    console.error('[awardMealGamification] error:', e);
  }
}
export async function deleteMealItem(itemId: string) {
  const { data, error } = await supabase.rpc('delete_meal_item', { item_id: itemId });
  if (error) throw error;
  return data as {
    success: boolean;
    meal_deleted?: boolean;
    error?: string;
  };
}
export async function deleteMeal(id: string) {
  const { error } = await supabase.from('meals').delete().eq('id', id);
  if (error) throw error;
}

// 🆕 Тип компонента блюда
export interface MealItemComponent {
  name: string;
  weight_g: number;
  calories: number;
  protein_g: number;
  fat_g: number;
  carbs_g: number;
}

export async function updateMealItem(
  itemId: string,
  data: {
    name: string;
    weight_g: number;
    calories: number;
    protein_g: number;
    fat_g: number;
    carbs_g: number;
    // 🆕 Опциональное поле — компоненты составного блюда
    // Если undefined — не передаётся в RPC, БД не трогает поле components
    // Если массив (даже пустой []) — обновляет components в БД
    components?: MealItemComponent[];
  }
) {
  // Готовим параметры для RPC
  const params: Record<string, any> = {
    p_item_id: itemId,
    p_name: data.name,
    p_weight_g: data.weight_g,
    p_calories: data.calories,
    p_protein_g: data.protein_g,
    p_fat_g: data.fat_g,
    p_carbs_g: data.carbs_g,
  };

  // 🆕 Передаём components только если они явно заданы
  if (data.components !== undefined) {
    params.p_components = data.components;
  }

  const { error } = await supabase.rpc('update_meal_item', params);
  if (error) throw error;
}
